import React, { useEffect, useState } from 'react';
import { CloseButton, FontWeight, Heading, Spinner, SpinnerColor, UserMessage } from '@lili/lili-frontend-common';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { FeatureAccessItem, RoleType } from '../../../../types';
import useStore from '../../../../store';
import { useGetPermissions } from '../../hooks/useGetPrmissions';

interface PermissionsListProps {
  onClose: () => void;
  type: RoleType;
}

export enum FeatureAccess {
  READ_WRITE = 'READ_WRITE',
  READ_ONLY = 'READ_ONLY',
  NONE = 'NONE',
}

export const PermissionsList = ({ type, onClose }: PermissionsListProps) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<FeatureAccessItem[]>([]);
  const { customerId } = useStore((state) => state.user);
  const { mutateAsync: getPermissions, isLoading } = useGetPermissions();
  useEffect(() => {
    getPermissions({ role: type, customerId }).then((r) => {
      const permissions = r.data as FeatureAccessItem[];
      setItems(permissions);
    });
  }, [customerId]);
  const accessTitle = (accessType: FeatureAccess): string => {
    switch (accessType) {
      case FeatureAccess.READ_WRITE:
        return t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS_LIST.ACCESS_TITLE.READ_WRITE');
      case FeatureAccess.NONE:
        return t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS_LIST.ACCESS_TITLE.NONE');
      case FeatureAccess.READ_ONLY:
        return t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS_LIST.ACCESS_TITLE.READ_ONLY');
      default:
        return '';
    }
  };
  const texts = () => {
    switch (type) {
      case RoleType.ACCOUNTANT:
        // TODO: Get description text from PM
        return {
          title: t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS_LIST.TITLE.ACCOUNTANT'),
          description: t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS_LIST.DESCRIPTION.ACCOUNTANT'),
        };
      case RoleType.ADMIN:
        // TODO: Get description text from PM
        return {
          title: t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS_LIST.TITLE.ADMIN'),
          description: t('ACCOUNT_SETTINGS.TEAM.TABLE.PERMISSIONS_LIST.DESCRIPTION.ADMIN'),
        };
      default:
        return {
          title: '',
          description: '',
        };
    }
  };

  return (
    <UserMessage id='permissions-list-modal' onClick={onClose}>
      <div className='bg-surface-primary-bw-00 rounded-xl w-[630px] overflow-hidden min-h-[566px]'>
        <div className='flex w-full px-[12px]  border-b-[1px] border-b-outline-primary-bw-03 justify-between'>
          <div className='flex flex-col gap-[4px] px-[12px]'>
            <Heading.H7 fontWeight={FontWeight.MEDIUM} className='text-text-main-primary mt-[25px]'>
              {texts().title}
            </Heading.H7>
            <Heading.H10 fontWeight={FontWeight.REGULAR} className='text-text-main-tertiary mb-[17px]'>
              {texts().description}
            </Heading.H10>
          </div>
          <CloseButton id='close-button' onClick={onClose} className='translate-y-[8px]' />
        </div>
        {isLoading ? (
          <Spinner height='2rem' width='2rem' spinnerColor={SpinnerColor.Blue} containerClassName='mt-[24px]' />
        ) : (
          <div className='overflow-y-scroll max-h-[566px] px-[24px]'>
            {items?.map((item, index) => (
              <div
                className={twMerge(
                  'w-full flex justify-between py-[12px] border-b-outline-primary-bw-03',
                  index === items.length - 1 ? 'border-b-[0px]' : 'border-b-[1px]',
                )}
              >
                <Heading.H10 fontWeight={FontWeight.REGULAR} className='text-text-main-primary'>
                  {item.localizedName}
                </Heading.H10>
                <Heading.H10 fontWeight={FontWeight.REGULAR} className='text-text-main-tertiary'>
                  {accessTitle(item.permission)}
                </Heading.H10>
              </div>
            ))}
          </div>
        )}
      </div>
    </UserMessage>
  );
};
